import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Mail, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toSafeDisplayString } from '../lib/apiErrors';
import {
  authCallbackUrl,
  clearPendingIntent,
  createPendingIntent,
  resolveAuthDestination,
} from '../lib/pendingIntent';

const safeAuthText = (value) => toSafeDisplayString(value, { context: 'find-jobs-submit' });

function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export default function AuthPage() {
  const { user, loading, signInWithOtp, verifyOtp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const authRedirectStartedRef = useRef(false);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState('main'); // 'main', 'email', 'sent'
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const attemptedReturnPath = new URLSearchParams(location.search).get('return_to') || '';
  const finishAuthNavigation = useCallback(() => {
    const destination = resolveAuthDestination({ attemptedReturnPath, consume: true });
    navigate(destination, { replace: true });
  }, [attemptedReturnPath, navigate]);

  useEffect(() => {
    if (!loading && user && !authRedirectStartedRef.current) {
      authRedirectStartedRef.current = true;
      finishAuthNavigation();
    }
  }, [finishAuthNavigation, loading, user]);

  if (loading || user || authRedirectStartedRef.current) {
    return (
      <div className="min-h-screen py-12 md:py-24" data-testid="auth-redirecting-state">
        <div className="max-w-md mx-auto px-6">
          <p className="text-muted-foreground text-sm">Signing you in…</p>
        </div>
      </div>
    );
  }

  const handleGoogleSignIn = async () => {
    if (isGoogleLoading) return;
    setIsGoogleLoading(true);
    setError('');
    try {
      createPendingIntent({ kind: 'auth_return', return_path: attemptedReturnPath });
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { error: gError } = await signInWithGoogle(authCallbackUrl());
      if (gError) throw gError;
      // Supabase redirects to Google — page will navigate away
    } catch (err) {
      setError(safeAuthText(err) || 'Failed to start Google sign-in');
      setIsGoogleLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email'); return; }
    setIsLoading(true);
    setError('');
    try {
      createPendingIntent({ kind: 'auth_return', return_path: attemptedReturnPath });
      const { error: otpError } = await signInWithOtp(email.toLowerCase().trim(), authCallbackUrl());
      if (otpError) throw otpError;
      setStep('sent');
    } catch (err) {
      setError(safeAuthText(err) || 'Failed to send sign-in link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!otpCode.trim() || otpCode.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setIsVerifying(true);
    setError('');
    try {
      const { error: verifyError } = await verifyOtp(email.toLowerCase().trim(), otpCode.trim());
      if (verifyError) throw verifyError;
      finishAuthNavigation();
    } catch (err) {
      setError(safeAuthText(err) || 'Invalid or expired code');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen py-12 md:py-24">
      <div className="max-w-md mx-auto px-6">
        <div className="mb-10">
          <Link to="/" onClick={clearPendingIntent} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight font-medium mb-3">
            Sign In
          </h1>
          <p className="text-muted-foreground text-lg">
            Access your dashboard and job matches.
          </p>
        </div>

        {/* ── Main sign-in view ──────────────────────────── */}
        {(step === 'main' || step === 'email') && (
          <div className="space-y-4">
            {/* Google sign-in — primary */}
            <Button
              onClick={handleGoogleSignIn}
              disabled={isGoogleLoading}
              className="w-full h-12 flex items-center justify-center gap-3 border border-border bg-background hover:bg-surface text-foreground font-medium rounded-lg transition-colors"
              data-testid="google-sign-in-btn"
            >
              <GoogleIcon className="w-5 h-5" />
              {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Email sign-in — collapsed by default, expandable */}
            {step === 'main' && (
              <button
                onClick={() => setStep('email')}
                className="w-full h-12 flex items-center justify-center gap-2 border border-border bg-background hover:bg-surface text-foreground text-sm font-medium rounded-lg transition-colors"
                data-testid="email-sign-in-toggle"
              >
                <Mail className="w-4 h-4" />
                Sign in with email link
              </button>
            )}

            {step === 'email' && (
              <div className="p-5 border border-border rounded-lg bg-surface/50" data-testid="auth-email-step">
                <form onSubmit={handleSendOtp} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="auth-email" className="text-sm">Email Address</Label>
                    <Input
                      id="auth-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-input text-base py-3"
                      data-testid="auth-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="btn-primary w-full"
                    disabled={isLoading}
                    data-testid="auth-send-otp-btn"
                  >
                    {isLoading ? 'Sending...' : 'Send Sign-In Link'}
                  </Button>
                </form>
                <button
                  onClick={() => { setStep('main'); setEmail(''); setError(''); }}
                  className="text-xs text-muted-foreground hover:text-foreground mt-3 transition-colors"
                >
                  Back to all options
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Email sent view ────────────────────────────── */}
        {step === 'sent' && (
          <div className="space-y-4" data-testid="auth-sent-step">
            <div className="p-5 border border-border rounded-lg bg-surface/50">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <h3 className="font-medium">Check your email</h3>
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                We sent a sign-in link to <strong>{email}</strong>.
              </p>
              <div className="p-3 bg-foreground/5 border border-border rounded-md space-y-1 mb-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ExternalLink className="w-4 h-4" />
                  Click the link in your email to sign in
                </div>
                <p className="text-xs text-muted-foreground">You&rsquo;ll be redirected back automatically.</p>
              </div>

              {/* OTP code fallback */}
              <div className="border-t border-border pt-4 mt-4">
                <p className="text-sm text-muted-foreground mb-3">Or enter the 6-digit code from the email:</p>
                <form onSubmit={handleVerifyCode} className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="form-input text-center text-2xl tracking-[0.5em] py-3"
                    maxLength={6}
                    data-testid="auth-otp-input"
                  />
                  <Button type="submit" className="btn-secondary w-full" disabled={isVerifying || otpCode.length !== 6} data-testid="auth-verify-btn">
                    {isVerifying ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </form>
              </div>
            </div>

            <div className="flex justify-between text-sm">
              <button onClick={() => { setStep('main'); setOtpCode(''); setError(''); }} className="text-muted-foreground hover:text-foreground transition-colors">
                Back to sign-in
              </button>
              <button onClick={handleSendOtp} disabled={isLoading} className="text-muted-foreground hover:text-foreground transition-colors">
                Resend link
              </button>
            </div>
          </div>
        )}

        {/* ── Error ──────────────────────────────────────── */}
        {error && (
          <div className="mt-4 p-4 border border-red-500/30 bg-red-500/5 rounded-lg" data-testid="auth-error">
            <p className="text-red-600 dark:text-red-400 text-sm">{safeAuthText(error)}</p>
          </div>
        )}

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Don&rsquo;t have an account? Sign in and one will be created automatically.
        </p>
      </div>
    </div>
  );
}
